import { colors } from '@/theme/foundations/colors';
import { Box, Spinner } from '@chakra-ui/react';
import React, { useRef, useState, useEffect } from 'react';

import {
  RPProvider,
  RPDefaultLayout,
  RPPages,
  RPConfig,
  ZoomLevel,
  RPTheme,
  RPLayout,
} from '@pdf-viewer/react';
import { FC, PropsWithChildren } from 'react';
import {
  SearchSm,
  ZoomIn,
  ZoomOut,
  Download01,
  Printer,
  Expand03,
} from '@kula-ai/icons-core';
import './pdfViewer.scss';
import { CustomOpenFileTool } from './CustomOpenFileTool';

interface PdfViewerProps {
  url: string;
  filename?: string | null | undefined;
  hideToolbar?: boolean;
  handleUpload?: any;
  showReplaceControl?: boolean;
  acceptedFiles?: string[];
  styles?: Record<string, any>;
  updateFromResumeControl?: React.ReactNode;
}

const customLightTheme = {
  '--rp-text-color': colors['secondary-700'],
  '--rp-toolbar-background': 'transparent',
  '--rp-toolbar-border-color': colors['secondary-200'],
  '--rp-button-hover-background': colors['secondary-50'],
  '--rp-pages-background-color': colors['secondary-50'],
  '--rp-dropdown-background-color': 'white',
  '--rp-popover-background-color': colors['secondary-25'],
  '--rp-highlight-background-color': 'orange',
  '--rp-tooltip-background-color': 'transparent',
  '--rp-dropdown-hover-background-color': colors['secondary-50'],
};

const LoaderImage = (
  <Spinner
    size="md"
    color={colors['primary-500']}
    thickness="2px"
    speed="0.75s"
  />
);

const ParentPdfViewer: FC<PropsWithChildren> = ({ children }) => {
  return (
    <RPConfig
      licenseKey={import.meta.env.VITE_PDF_VIEWER_LICENSE || ''}
      workerUrl="https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs"
    >
      <RPTheme customVariables={customLightTheme}>{children}</RPTheme>
    </RPConfig>
  );
};

export const PdfViewer = ({
  url,
  filename,
  hideToolbar = false,
  handleUpload = () => {},
  showReplaceControl = false,
  acceptedFiles = [],
  styles = {},
  updateFromResumeControl,
}: PdfViewerProps) => {
  const name = filename ?? 'document';
  const controlRef = useRef<HTMLDivElement>(null);
  const [controlWidth, setControlWidth] = useState(0);

  useEffect(() => {
    if (controlRef.current) {
      setControlWidth(controlRef.current.offsetWidth);
    }
  }, [updateFromResumeControl]);

  const handleFileSelect = async (file: File) => {
    if (!handleUpload || !file) return;
    try {
      handleUpload(file);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };
  return (
    <ParentPdfViewer>
      <RPProvider
        src={url}
        loaderImage={LoaderImage}
        initialScale={ZoomLevel.PAGE_WIDTH}
        downloadFilename={name}
      >
        {hideToolbar ? (
          <RPLayout>
            <RPPages />
          </RPLayout>
        ) : (
          <Box
            position="relative"
            borderRadius="sm"
            border="1px solid"
            borderColor="secondary-200"
            pb="2px"
            h="100%"
            sx={
              controlWidth
                ? {
                    "div[data-rp='topBarRight']": {
                      marginRight: `${controlWidth + 8}px !important`,
                    },
                  }
                : {}
            }
            {...styles?.container}
          >
            <RPDefaultLayout
              style={{ height: '100%', ...styles?.layout }}
              slots={{
                sidebarEnable: false,
                openFileTool: showReplaceControl
                  ? (props) => (
                      <CustomOpenFileTool
                        {...props}
                        onFileSelect={handleFileSelect}
                        accept={
                          acceptedFiles.length
                            ? acceptedFiles.join(',')
                            : 'application/pdf'
                        }
                      />
                    )
                  : false,
                thumbnailTool: false,
                themeSwitcher: false,
                pageNavigationTool: false,
                documentProperties: false,
              }}
              mobileWidth={600}
              icons={{
                searchIcon: <SearchSm size={20} />,
                zoomInIcon: <ZoomIn size={20} />,
                zoomOutIcon: <ZoomOut size={20} />,
                downloadIcon: <Download01 size={20} />,
                printIcon: <Printer size={20} />,
                fullScreenIcon: <Expand03 size={20} />,
              }}
            >
              <RPPages />
            </RPDefaultLayout>
            {updateFromResumeControl && (
              <Box
                ref={controlRef}
                position="absolute"
                top="11px"
                right="4px"
                zIndex={1}
                visibility={controlWidth ? 'visible' : 'hidden'}
              >
                {updateFromResumeControl}
              </Box>
            )}
          </Box>
        )}
      </RPProvider>
    </ParentPdfViewer>
  );
};
